import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Thread, ThreadList } from '../../data-contract/chat';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { getThreadStatus, THREAD_STATUS_REFETCH_INTERVAL_MS, type ThreadStatus } from '../thread-status';
import { chatKeys } from './query-keys';

type UseChatThreadsOptions = {
    readonly enabled?: boolean;
    readonly getStatus?: (thread: Thread) => ThreadStatus;
};

export const useChatThreads = (options?: UseChatThreadsOptions): UseQueryResult<ThreadList, Error> => {
    const { fetchChatThreads } = useApplicationFetchers();
    const getStatus = options?.getStatus ?? getThreadStatus;

    return useQuery({
        enabled: options?.enabled ?? true,
        queryFn: async ({ signal }) => {
            const result = await fetchChatThreads({ signal });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: chatKeys.threads(),
        // Only 'running' changes server-side without a user action; needs-input/on-hold must not keep polling.
        refetchInterval: (query) =>
            query.state.data?.threads.some((thread) => getStatus(thread) === 'running') === true
                ? THREAD_STATUS_REFETCH_INTERVAL_MS
                : false,
    });
};
